import { useCallback, useMemo } from 'react'
import { RotateCcw, Users } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchReferralAbuse, clawbackReferral, FRAUD_STR as S, type ReferralAbuseRow } from './fraudApi'

/**
 * A25.14 — Злоупотребление рефералами. Clusters of one inviter + many fake
 * invitees (often same device). Claw back the reward. Dormant until
 * `/admin/antifraud/referral-abuse` ships. Placeholder permission USERS_VIEW.
 */
export function ReferralAbuseScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<ReferralAbuseRow>[]>(
    () => [
      { key: 'inviter', header: S.inviter, type: 'text' },
      { key: 'invitees', header: S.invitees, type: 'number' },
      { key: 'same_device', header: S.sameDevice, type: 'number' },
      { key: 'reward', header: S.reward, type: 'number' },
      { key: 'status', header: S.status, type: 'text' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<ReferralAbuseRow>[]>(
    () => [
      {
        id: 'clawback',
        label: S.clawback,
        icon: <RotateCcw size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: async (r) => {
          try {
            await clawbackReferral(r.id)
            toast(S.clawedBack, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchReferralAbuse, [])
  return (
    <>
      <PageHeader title={S.referralAbuse} screenId="A25.14" />
      <DataTable<ReferralAbuseRow>
        screenId="A25.14"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Users size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
